import React, { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Drawer, Input } from "antd5";

import Button from "components/ui/button/Button";
import { TrackingProvider } from "lib/tracking";
import { Flex, Text } from "../../../styles/utility-components";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  initialTitle?: string;
  initialQuestion?: string;
  onSave: (title: string, question: string) => void;
};

export function AddOrEditCriteriaDrawer({
  isOpen,
  onClose,
  initialTitle,
  initialQuestion,
  onSave,
}: Props) {
  const type = initialTitle && initialQuestion ? "Edit" : "Add";
  const [title, setTitle] = useState(initialTitle);
  const [question, setQuestion] = useState(initialQuestion);

  const { colors } = useTheme();

  useEffect(() => {
    setTitle(initialTitle);
    setQuestion(initialQuestion);
  }, [initialTitle, initialQuestion]);

  const closeDrawer = () => {
    setTitle("");
    setQuestion("");
    onClose();
  };

  return (
    <Drawer
      placement="right"
      closable={true}
      open={isOpen}
      width={440}
      onClose={(event) => {
        // This is triggered when they click on the mask outside the drawer
        event.stopPropagation();
        onClose();
      }}
      styles={{ header: { display: "none" }, body: { padding: "24px" } }}
    >
      <TrackingProvider data={{ "Action type": type }}>
        <Flex justifyContent="space-between" height="100%" flexDirection="column">
          <Flex height="100%" flexDirection="column">
            <Flex gap={16} flexDirection="column">
              <Text h3>{type} criteria</Text>
              <Text color={colors.sysTextSecondary}>
                Use the form below to add a new requirement summary question.
              </Text>
            </Flex>
            <Flex gap={32} flexDirection="column">
              <Flex gap={4} flexDirection="column">
                <Text fontWeight="500">Requirement title</Text>
                <Description>How you want this criteria to display in the report</Description>
                <Input
                  placeholder="Add a title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </Flex>
              <Flex gap={4} flexDirection="column">
                <Text fontWeight="500">Detailed question</Text>
                <Description>
                  What are you trying to understand from within the tender documents? Phrase this as
                  a question. The more specific & precise the better
                </Description>
                <Input.TextArea
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  autoFocus
                  autoSize
                  placeholder="Add a detailed question"
                />
              </Flex>
            </Flex>
          </Flex>
          <Flex gap="8px" alignItems="center" justifyContent="flex-end">
            <Button buttonType="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!title || !question}
              onClick={() => {
                if (title && question) {
                  onSave(title, question);
                  closeDrawer();
                }
              }}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </TrackingProvider>
    </Drawer>
  );
}

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  fontSize: 12,
}));
